'use client'

import { parseBlocks, type Inline } from '@/lib/ai/lite-markdown'

function renderInline(nodes: Inline[], keyPrefix = ''): React.ReactNode[] {
  return nodes.map((n, i) => {
    const key = `${keyPrefix}${i}`
    switch (n.type) {
      case 'text':
        return <span key={key}>{n.text}</span>
      case 'bold':
        return <strong key={key} className="font-semibold">{renderInline(n.children, `${key}.`)}</strong>
      case 'italic':
        return <em key={key}>{renderInline(n.children, `${key}.`)}</em>
      case 'code':
        return (
          <code key={key} className="rounded bg-background/60 border border-border px-1 py-0.5 font-mono text-[0.85em]">
            {n.text}
          </code>
        )
      case 'link':
        return (
          <a
            key={key}
            href={n.href}
            className="underline underline-offset-2 decoration-primary/60 hover:decoration-primary text-primary"
            target={n.href.startsWith('/') ? undefined : '_blank'}
            rel={n.href.startsWith('/') ? undefined : 'noopener noreferrer'}
          >
            {renderInline(n.children, `${key}.`)}
          </a>
        )
    }
  })
}

/**
 * Renders a Biggles reply. Assistant text only — what the user typed is shown
 * literally. Streaming-safe: an unclosed `**` mid-stream is just text until
 * its partner arrives, so nothing flickers into bold and back.
 */
export function BigglesMarkdown({ text }: { text: string }) {
  const blocks = parseBlocks(text)
  return (
    <div className="space-y-2 break-words">
      {blocks.map((b, i) => {
        switch (b.type) {
          case 'paragraph':
            return (
              <p key={i} className="whitespace-pre-wrap">
                {renderInline(b.children, `${i}.`)}
              </p>
            )
          case 'heading':
            return (
              <p key={i} className="font-semibold">
                {renderInline(b.children, `${i}.`)}
              </p>
            )
          case 'quote':
            return (
              <blockquote key={i} className="border-l-2 border-border pl-3 text-muted-foreground">
                {renderInline(b.children, `${i}.`)}
              </blockquote>
            )
          case 'table':
            return (
              <div key={i} className="overflow-x-auto">
                <table className="text-[0.9em] border-collapse">
                  <thead>
                    <tr>
                      {b.header.map((cell, j) => (
                        <th key={j} className="border border-border px-2 py-1 text-left font-semibold">
                          {renderInline(cell, `${i}.h${j}.`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.rows.map((row, r) => (
                      <tr key={r}>
                        {row.map((cell, c) => (
                          <td key={c} className="border border-border px-2 py-1 align-top">
                            {renderInline(cell, `${i}.${r}.${c}.`)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          case 'image':
            // Chat has no asset store — the alt text is the whole picture here.
            return (
              <p key={i} className="text-muted-foreground italic">
                {b.alt}
              </p>
            )
          case 'list': {
            const Tag = b.ordered ? 'ol' : 'ul'
            return (
              <Tag key={i} className={`${b.ordered ? 'list-decimal' : 'list-disc'} pl-5 space-y-1`}>
                {b.items.map((item, j) => (
                  <li key={j}>{renderInline(item, `${i}.${j}.`)}</li>
                ))}
              </Tag>
            )
          }
        }
      })}
    </div>
  )
}
